import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { FieldArray } from 'react-final-form-arrays';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { FieldCheckbox, ValidationError, HelpText } from '../../components';

import css from './FieldMultiSelect.module.css';

// Long lists get a type-to-filter box; below this a list is faster to scan.
const SEARCHABLE_FROM_OPTION_COUNT = 10;

/**
 * Extracts a plain text label from an option, handling string labels,
 * textLabel overrides, and React element labels (e.g. ColorSwatchLabel).
 */
const getOptionLabel = option => {
  if (!option) {
    return '';
  }
  if (typeof option.textLabel === 'string') {
    return option.textLabel;
  }
  if (typeof option.label === 'string') {
    return option.label;
  }
  if (React.isValidElement(option.label) && typeof option.label.props?.label === 'string') {
    return option.label.props.label;
  }
  if (typeof option.key === 'string') {
    return option.key;
  }
  return '';
};

/**
 * Summary shown on the trigger: comma-separated labels of selected options, or placeholder.
 */
const getSummary = (selected, options, placeholder) => {
  if (!selected || selected.length === 0) {
    return placeholder;
  }
  const labels = selected
    .map(key => {
      const match = options.find(o => `${o.key}` === `${key}`);
      return match ? getOptionLabel(match) : key;
    })
    .filter(Boolean);

  return labels.length > 0 ? labels.join(', ') : placeholder;
};

const MultiSelectRenderer = props => {
  const intl = useIntl();
  const {
    className,
    rootClassName,
    label,
    helpText,
    id,
    fields,
    options,
    placeholder,
    meta,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const onDocumentClick = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    const onKeyDown = e => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        // Return focus to the trigger, or focus is lost to the document body.
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDocumentClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocumentClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  const selected = fields.value || [];
  const isSearchable = options.length >= SEARCHABLE_FROM_OPTION_COUNT;
  const normalized = query.trim().toLowerCase();
  const visibleOptions =
    isSearchable && normalized
      ? options.filter(
          o =>
            getOptionLabel(o).toLowerCase().includes(normalized) ||
            // Keep ticked options visible, so filtering never hides a selection
            // the user cannot then see to undo.
            selected.includes(o.key)
        )
      : options;

  const defaultPlaceholder = intl.formatMessage(
    { id: 'FieldSelect.placeholder' },
    { defaultMessage: 'Select…' }
  );
  const summary = getSummary(selected, options, placeholder || defaultPlaceholder);
  const hasSelection = selected.length > 0;
  const listId = `${id}.list`;
  const { touched, invalid, error } = meta || {};
  const hasError = touched && invalid && error;

  return (
    <div className={classNames(rootClassName || css.root, className)} ref={wrapperRef}>
      {label ? (
        <label className={css.label} htmlFor={`${id}.trigger`}>
          {label}
        </label>
      ) : null}

      <button
        type="button"
        id={`${id}.trigger`}
        ref={triggerRef}
        className={classNames(css.trigger, {
          [css.triggerOpen]: isOpen,
          [css.triggerError]: hasError,
        })}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={isOpen ? listId : undefined}
        onClick={() => setIsOpen(open => !open)}
      >
        <span className={classNames(css.summary, { [css.placeholder]: !hasSelection })}>
          {summary}
        </span>
        <span className={css.caret} aria-hidden="true" />
      </button>

      {isOpen ? (
        <div className={css.popover}>
          {isSearchable ? (
            <input
              type="search"
              className={css.search}
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={intl.formatMessage({ id: 'FieldMultiSelect.searchPlaceholder' })}
              aria-label={intl.formatMessage(
                { id: 'FieldMultiSelect.searchLabel' },
                { fieldLabel: label }
              )}
            />
          ) : null}

          {visibleOptions.length === 0 ? (
            <p className={css.noMatches}>
              <FormattedMessage id="FieldMultiSelect.noMatches" values={{ query: query.trim() }} />
            </p>
          ) : (
            <ul className={css.list} id={listId}>
              {visibleOptions.map(option => (
                <li key={`${id}.${option.key}`} className={css.item}>
                  <FieldCheckbox
                    id={`${id}.${option.key}`}
                    name={fields.name}
                    label={option.label}
                    value={option.key}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      <HelpText helpText={helpText} />
      <ValidationError fieldMeta={{ ...meta }} />
    </div>
  );
};

/**
 * Multi-select rendered as a dropdown of checkboxes.
 *
 * A drop-in alternative to FieldCheckboxGroup for fields with many options,
 * where a flat checkbox list becomes a wall of rows. Selection state is still
 * an array of option keys held by react-final-form, so the surrounding form,
 * validation and submit handling are unchanged.
 *
 * @component
 * @param {Object} props
 * @param {string} props.name form field name
 * @param {string} props.id
 * @param {Array<{key: string, label: React.Node}>} props.options
 * @param {React.Node} [props.label]
 * @param {string} [props.placeholder] shown when nothing is selected
 * @param {React.Node} [props.helpText]
 * @param {Function} [props.validate]
 * @returns {JSX.Element}
 */
const FieldMultiSelect = props => <FieldArray component={MultiSelectRenderer} {...props} />;

export default FieldMultiSelect;
